import { QuoteProductCommentSide, QuoteProductThreadKind } from '@codezo/graphql';
import { ThreadPanel, ThreadView, type ThreadMessage } from '@codezo/product-comments';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useProductThread } from '../../../hooks/useProductThread';
import { timeAgo } from '../../../lib/utils';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { FilePreview, type FilePreviewItem } from '../../ui/file-preview';
import type { QuoteThread } from './useQuoteThreads';

export const threadLabel = (t: QuoteThread) =>
	t.kind === QuoteProductThreadKind.Studio
		? 'Studio notes'
		: (t.vetterEntity?.name ?? 'Supplier');

/**
 * A product's threads, one tab each: the studio's own notes (never shown to a
 * supplier), then one per supplier asked to price it. Opened from the product
 * row's comments button, on the quote page and the order page alike.
 */
export function ProductThreadsPanel({
	productName,
	threads,
	activeThread,
	unreadOf,
	onTabChange,
	onClose,
	onRead,
}: {
	productName: string;
	threads: QuoteThread[];
	activeThread: QuoteThread | null;
	unreadOf: (t: QuoteThread) => number;
	onTabChange: (threadId: string) => void;
	onClose: () => void;
	onRead: (threadId: string) => void;
}) {
	return (
		<ThreadPanel
			open
			onClose={onClose}
			title={productName}
			subtitle={
				activeThread?.kind === QuoteProductThreadKind.Supplier
					? `With ${threadLabel(activeThread)} — they see this thread`
					: 'Studio only — suppliers never see these notes'
			}
			tabs={threads.map((t) => ({
				id: t.id,
				label: threadLabel(t),
				unread: unreadOf(t),
				count: t.commentCount,
			}))}
			activeTab={activeThread?.id}
			onTabChange={onTabChange}
		>
			{activeThread ? (
				// Keyed on the thread: switching tabs starts that thread fresh — its
				// own messages, draft and read state.
				<ActiveThread key={activeThread.id} thread={activeThread} onRead={onRead} />
			) : (
				<div className="px-4 py-10 text-center text-sm text-text-4">
					No threads on this product yet.
				</div>
			)}
		</ThreadPanel>
	);
}

function ActiveThread({
	thread: t,
	onRead,
}: {
	thread: QuoteThread;
	onRead: (threadId: string) => void;
}) {
	const isStudio = t.kind === QuoteProductThreadKind.Studio;
	const supplier = t.vetterEntity?.name ?? 'the supplier';
	const thread = useProductThread({
		orderQuoteItemId: t.orderQuoteItemId,
		vetterEntityId: t.vetterEntityId,
		onRead: () => onRead(t.id),
	});
	const [viewer, setViewer] = useState<{ files: FilePreviewItem[]; index: number } | null>(null);
	const [deleteId, setDeleteId] = useState<string | null>(null);

	const openAttachment = (message: ThreadMessage, index: number) => {
		const files = message.attachments
			.map((a, i) => ({ a, i }))
			.filter(({ a }) => !!a.url)
			.map(({ a, i }) => ({
				id: `${message.id}-${i}`,
				url: a.url as string,
				name: a.name,
				mimeType: a.mimeType,
				size: a.size,
			}));
		const at = files.findIndex((f) => f.id === `${message.id}-${index}`);
		if (at >= 0) setViewer({ files, index: at });
	};

	return (
		<>
			<ThreadView
				className="flex-1"
				loading={thread.loading}
				onReachBottom={thread.reachedBottom}
				messages={{
					messages: thread.messages,
					labels: {
						formatTime: timeAgo,
						sideLabel: (side) =>
							side === QuoteProductCommentSide.Supplier ? supplier : null,
					},
					emptyState: isStudio
						? 'Notes for the studio about this product. Tag someone to notify them.'
						: `Ask ${supplier} about this product — they're notified when you post.`,
					onOpenAttachment: openAttachment,
					editingId: thread.editingId,
					candidates: thread.candidates,
					onEditSave: thread.saveEdit,
					onEditCancel: () => thread.setEditingId(null),
					editSaving: thread.editSaving,
					messageActions: (m) =>
						m.canEdit || m.canDelete ? (
							<DropdownMenu>
								<DropdownMenuTrigger
									aria-label="Message actions"
									className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<MoreHorizontal size={14} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align={m.isMine ? 'end' : 'start'}>
									{m.canEdit && (
										<DropdownMenuItem onClick={() => thread.setEditingId(m.id)}>
											Edit
										</DropdownMenuItem>
									)}
									{m.canDelete && (
										<DropdownMenuItem
											variant="destructive"
											onClick={() => setDeleteId(m.id)}
										>
											Delete
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						) : null,
				}}
				readOnlyNote="This conversation is no longer available."
				composer={
					thread.ended
						? undefined
						: {
								candidates: thread.candidates,
								pendingFiles: thread.pendingFiles,
								onAddFiles: thread.addFiles,
								onRemoveFile: thread.removeFile,
								onSend: thread.send,
								sending: thread.sending,
								preparing: thread.preparing,
								// What sending does, from the backend's notification rules.
								hint: isStudio
									? 'Only people you tag are notified'
									: `Notifies ${supplier} — by bell, or email for link-only people`,
							}
				}
			/>
			{viewer && (
				<FilePreview
					files={viewer.files}
					initialIndex={viewer.index}
					onClose={() => setViewer(null)}
				/>
			)}
			<ConfirmDialog
				open={!!deleteId}
				onOpenChange={(o) => !o && setDeleteId(null)}
				title="Delete this message?"
				description="It's replaced by “Message deleted” for everyone in the thread."
				confirmLabel="Delete"
				onConfirm={async () => {
					if (deleteId) await thread.remove(deleteId);
					setDeleteId(null);
				}}
			/>
		</>
	);
}
